<script setup lang="ts">
import gsap from 'gsap'

const props = defineProps<{
  data: SectionHeroRecord
  moduleIndex: number
}>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  if (ctxRef.value) {
    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context(() => {
        useLenisSpeed()
      }, ctxRef.value as HTMLElement)
    })
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="pb-12 pt-header-h lg:pb-36 lg:pt-header-h-lg"
    :class="[
      invertedStyles,
      {
        'max-h-[680px] min-h-screen w-full overflow-hidden lg:max-h-none lg:min-h-[800px]':
          data.layout === 'Centered' && data.mediaFit === 'Cover'
      }
    ]"
  >
    <template v-if="data.layout === 'Left'">
      <div
        class="mx-auto mb-8 mr-0 mt-12 aspect-4/3 max-w-full object-cover px-4 md:aspect-cinema md:pr-0 lg:-mb-32 lg:mt-0 lg:max-w-6xl lg:pl-10"
      >
        <Image
          v-if="data.media"
          :preload="moduleIndex === 0"
          class="aspect-4/3 object-cover md:hidden md:aspect-cinema"
          :src="data.media.responsiveImage ? data.media.responsiveImage?.src : data.media.url"
          :srcset="data.media.responsiveImage ? data.media.responsiveImage?.webpSrcSet : undefined"
          :width="
            data.media.responsiveImage
              ? data.media.responsiveImage?.width
              : (data.media.width ?? 1280)
          "
          :height="
            data.media.responsiveImage
              ? data.media.responsiveImage?.height
              : (data.media.height ?? 960)
          "
          :alt="data.media?.alt ?? ''"
          sizes="(min-width: 1400px) 1280px, 93.7vw"
        />
        <Image
          v-if="data.media"
          :preload="moduleIndex === 0"
          class="hidden h-[510px] w-[1105px] object-cover md:block"
          :src="data.cinemaRatio ? data.cinemaRatio?.src : data.media.url"
          :srcset="data.cinemaRatio ? data.cinemaRatio?.webpSrcSet : undefined"
          :width="data.cinemaRatio ? data.cinemaRatio?.width : (data.media.width ?? 1105)"
          :height="data.cinemaRatio ? data.cinemaRatio?.height : (data.media.height ?? 510)"
          :alt="data.media?.alt ?? ''"
          sizes="auto"
        />
      </div>
      <div class="container relative">
        <div v-if="data.label" class="mb-2 text-sm uppercase opacity-40 lg:mb-4 lg:text-xl">
          <TextReveal>{{ data.label }}</TextReveal>
        </div>
        <Heading
          v-if="data.title"
          class="mt-4 max-w-[678px] uppercase lg:mt-8"
          :level="level"
          :size="2"
          :is-semantic="isSemantic"
        >
          <TextReveal :delay="0.25">{{ data.title }}</TextReveal>
        </Heading>
        <div v-if="data.subtitle" class="mt-4 max-w-lg lg:mt-8 lg:text-xl">
          <TextReveal :delay="0.9">{{ data.subtitle }}</TextReveal>
        </div>
        <CallToAction
          v-if="Array.isArray(data.buttons) && data.buttons.length"
          :data="data.buttons"
          class="mt-4 lg:mt-8"
        />
      </div>
    </template>
    <template v-if="data.layout === 'Centered'">
      <Image
        v-if="data.media && data.mediaFit === 'Cover'"
        :preload="moduleIndex === 0"
        class="absolute bottom-0 left-1/2 h-auto w-[640px] max-w-none -translate-x-1/2 object-contain lg:bottom-auto lg:top-[-300px] lg:w-[2100px]"
        :src="data.standardRatio"
        :width="
          data.media.responsiveImage
            ? data.media.responsiveImage?.width
            : (data.media.width ?? 1440)
        "
        :height="
          data.media.responsiveImage
            ? data.media.responsiveImage?.height
            : (data.media.height ?? 1090)
        "
        :alt="data.media?.alt ?? ''"
        :data-speed="0.75"
        sizes="(min-width: 2940px) 2720px, 93.59vw"
      />
      <svg
        class="absolute left-[20%] top-0 hidden"
        width="165"
        height="498"
        viewBox="0 0 165 498"
        fill="none"
      >
        <path
          d="M163.536 498C163.536 315 155.5 107.5 1 0.5"
          stroke="url(#paint0_linear_1248_4151)"
        />
        <defs>
          <linearGradient
            id="paint0_linear_1248_4151"
            x1="88.3147"
            y1="-75.821"
            x2="196.469"
            y2="-69.2374"
            gradientUnits="userSpaceOnUse"
          >
            <stop stop-color="#FF69E7" />
            <stop offset="0.345" stop-color="#A168FF" stop-opacity="0.55" />
            <stop offset="0.65" stop-color="#68E4FF" stop-opacity="0.3" />
            <stop offset="1" stop-color="#7DFF68" />
          </linearGradient>
        </defs>
      </svg>
      <div class="container relative lg:text-center">
        <div v-if="data.label" class="mb-2 text-sm uppercase opacity-40 lg:mb-4 lg:text-xl">
          <TextReveal>{{ data.label }}</TextReveal>
        </div>
        <Heading
          v-if="data.title"
          class="mt-14 max-w-5xl uppercase lg:mx-auto lg:mt-8"
          :level="level"
          :size="2"
          :is-semantic="isSemantic"
        >
          <TextReveal :delay="0.5">{{ data.title }}</TextReveal>
        </Heading>
        <div v-if="data.subtitle" class="mt-4 max-w-4xl lg:mx-auto lg:mt-8 lg:text-xl">
          <TextReveal :delay="0.8">{{ data.subtitle }}</TextReveal>
        </div>
        <CallToAction
          v-if="Array.isArray(data.buttons) && data.buttons.length"
          :data="data.buttons"
          class="mt-4 lg:mx-auto lg:mt-8"
        />
        <Image
          v-if="data.media && data.mediaFit === 'Contain'"
          class="mx-auto mt-8 aspect-video object-cover lg:mt-12 lg:aspect-cinema"
          :preload="moduleIndex === 0"
          :src="data.cinemaRatio ? data.cinemaRatio?.src : data.media.url"
          :srcset="data.cinemaRatio ? data.cinemaRatio?.webpSrcSet : undefined"
          :width="data.cinemaRatio ? data.cinemaRatio?.width : (data.media.width ?? 1105)"
          :height="data.cinemaRatio ? data.cinemaRatio?.height : (data.media.height ?? 510)"
          :alt="data.media?.alt ?? ''"
          sizes="(min-width: 1440px) 1440px, 100vw"
        />
      </div>
    </template>
  </div>
</template>
